//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region Angular Imports

import { StateProvider, StateParams } from "@uirouter/angularjs"

//#endregion

import { EventsNames, ConvertHelper, ReferenceTypeComponent, LogHelper } from "../../helpers"

import { Enums } from "../../models/common"
import { IOpObject } from '../../models/util/interfaces'

import { DetailsOptions } from "../../modules/common/details-popup/details-popup.directive"
import { MenuItem } from "../../modules/common/sidebar-menu/common.sidebar-menu.directive"
import { CreateEditInitParams, BulkEditInitParams } from "../../modules/common/create-edit-popup/create-edit-popup.controller"

import { $ROOT_SCOPE_SERVICE_NAME, BOWSER_SERVICE_NAME, CreateEditResult, DetailsResult, OpenDetailsOrModuleOptions } from './system.service.interfaces'


// #region Public Types

export const SERVICE_NAME = 'SystemService'
export const PROVIDER_NAME = 'SystemServiceProvider'

export interface RegisterDetailsComponentOptions {
    componentName: string,
    key: string,
}

export interface RegisterMenuItemOptions {
    iconName: string,
    index: number
    name: string,
    caption: string,
    resource: string,
    stateName: string,
    params?: any[]
}

export interface RegisterStateOptions {
    name: string,
    url: string,
    controller: string,
    template: string,
    params: Record<string, any>
}

// #endregion

export class SystemService {

    //#region Consts

    private get LOG_PREFIX(): string { return '[SIMAX.Services.System]' }

    //#endregion


    //#region Ctor

    constructor(
        private detailsComponentsOptions: RegisterDetailsComponentOptions[],
        private $rootScope: ng.IRootScopeService,
        private $stateParams: StateParams,
        private bowser: ng.bowser.IBowserService
    ) { }

    //#endregion

    //#region Browser

    public getBrowserName(): Enums.Browsers {
        const browserName: string = this.bowser.name.replace(' ', '')

        return ConvertHelper.parseEnum<Enums.Browsers>(Enums.Browsers, browserName)
    }

    //#endregion

    //#region Create Edit

    public openCreateEdit<TObject extends IOpObject = any>(options: CreateEditInitParams): JQueryPromise<CreateEditResult<TObject>> {
        const deferred = $.Deferred();

        this.$rootScope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, options, (result: CreateEditResult<TObject>) => {
            deferred.resolve(result);
        });

        return deferred.promise();
    }

    public openBulkCreateEdit<TObject extends IOpObject = any>(options: BulkEditInitParams): JQueryPromise<CreateEditResult<TObject>> {
        const deferred = $.Deferred();

        this.$rootScope.$broadcast(EventsNames.INITIALIZE_BULK_EDIT_POPUP, options, (result: CreateEditResult<TObject>) => {
            deferred.resolve(result);
        });

        return deferred.promise();
    }

    //#endregion

    //#region Details

    public getDetailsComponentOptions(): RegisterDetailsComponentOptions[] {
        return this.detailsComponentsOptions
    }

    public openDetails(options: DetailsOptions): JQueryPromise<DetailsResult> {
        if (options != null) {

            let moduleName: Enums.SimaxModuleName = options.moduleName

            if (options.idReferenceType != null) {
                moduleName = ReferenceTypeComponent.getModuleByReferenceType(options.idReferenceType)
            }

            options.moduleName = moduleName

            if (moduleName != null) {
                const deferred = $.Deferred();

                switch (moduleName) {
                    case Enums.SimaxModuleName.Action:
                    case Enums.SimaxModuleName.Meter_Alarm:
                        this.$rootScope.$broadcast(
                            EventsNames.OPEN_DETAILS_OR_MODULE,
                            ReferenceTypeComponent.getReferenceTypeByModule(moduleName),
                            options.pageItemsIds || [options.referenceObjectId],
                            options.referenceObjectId
                        )

                        break;

                    default:
                        this.$rootScope.$broadcast(
                            EventsNames.OPEN_DETAILS_OR_MODULE,
                            options,
                            (e) => {
                                deferred.resolve(e)
                            }
                        )

                        break;
                }

                return deferred.promise();
            }

            LogHelper.warn(this.LOG_PREFIX, 'Unable to open details. No refrenceType or module name given.')
        }

        return null;
    }

    public openDetailsOrModule(options: OpenDetailsOrModuleOptions): void {
        if (options != null) {
            let moduleName: Enums.SimaxModuleName = options.moduleName

            if (options.idReferenceType != null) {
                moduleName = ReferenceTypeComponent.getModuleByReferenceType(options.idReferenceType)
            }

            if (moduleName != null) {
                switch (moduleName) {
                    case Enums.SimaxModuleName.Action:
                    case Enums.SimaxModuleName.Alarm_Group:
                    case Enums.SimaxModuleName.Meter_Alarm:
                        this.$rootScope.$broadcast(
                            EventsNames.OPEN_DETAILS_OR_MODULE,
                            ReferenceTypeComponent.getReferenceTypeByModule(moduleName),
                            options.referenceObjectIds,
                            options.referenceObjectIds
                        )

                        break;

                    default:
                        this.$rootScope.$broadcast(
                            EventsNames.OPEN_DETAILS_OR_MODULE,
                            {
                                moduleName,
                                referenceObjectIds: options.referenceObjectIds,
                            }
                        )

                        break;
                }
            } else {
                LogHelper.warn(this.LOG_PREFIX, 'Unable to open details or module. No refrenceType or module name given.')
            }
        }
    }

    //#endregion 


    //#region Loader

    public showLoadIndicator(): void {
        this.$rootScope.$broadcast('showLoadingIndicator')
    }

    public hideLoadIndicator(): void {
        this.$rootScope.$broadcast('hideLoadingIndicator')
    }

    //#endregion


    //#region State

    public getStateParams(): StateParams {
        return this.$stateParams;
    }

    //#endregion
}

export class SystemServiceProvider implements ng.IServiceProvider {

    // #region Members

    private detailsComponentsOptions: RegisterDetailsComponentOptions[] = []
    private stateOptions: RegisterStateOptions[] = []

    // #endregion

    public constructor(private readonly $stateProvider: StateProvider) { }

    public registerDetailsComponent = (options: RegisterDetailsComponentOptions) => {
        this.detailsComponentsOptions.push(options)
    }

    public registerMenuItem = (options: RegisterMenuItemOptions) => {
        MenuItem.addMenuItem(
            options.iconName,
            options.name,
            options.resource,
            options.stateName,
            options.index,
            options.caption,
            options.params
        )
    }

    public registerState = (options: RegisterStateOptions) => {
        if (options != null) {
            this.stateOptions.push(options)
        }
    }

    // #region IServiceProvider Implementation 

    public $get = [
        $ROOT_SCOPE_SERVICE_NAME,
        '$stateParams',
        BOWSER_SERVICE_NAME,
        (
            $rootScope: ng.IRootScopeService,
            $stateParams: StateParams,
            BOWSER
        ) => {
            _.forEach(this.stateOptions, (stateOption: RegisterStateOptions) => {
                this.$stateProvider.state(stateOption.name, {
                    url: stateOption.url,
                    controller: stateOption.controller,
                    template: stateOption.template,
                    params: stateOption.params
                })
            })

            return new SystemService(this.detailsComponentsOptions, $rootScope, $stateParams, BOWSER)
        }]

    // #endregion
} 